// frontend/src/pages/OLTOnus.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { deleteONU } from "../api/olt.api";

const LOW_SIGNAL_MIN = -25;
const LOW_SIGNAL_MAX = -12;

export default function OLTOnus() {
  const { oltId } = useParams();
  const navigate = useNavigate();

  const [oltName, setOltName] = useState("");
  const [onus, setOnus] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [powerFilter, setPowerFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    status: "all",
    power: "all",
  });

  // ====== PAGINACIÓN ======
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // ========================
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(`/olts/${oltId}/onus`, {
        params: {
          page,
          limit,
          search: search.trim() || undefined,
          status: appliedFilters.status !== "all" ? appliedFilters.status : undefined,
          power: appliedFilters.power !== "all" ? appliedFilters.power : undefined,
        },
      });
      setOltName(res.data?.olt_name || `OLT #${oltId}`);
      setOnus(res.data?.onus || []);
      setTotal(res.data?.total || 0);
      setPages(res.data?.pages || 1);
    } catch (err) {
      console.error("Error cargando ONUs:", err);
      setOnus([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [oltId, page, limit, search, appliedFilters]);

  // 👇 FUNCIÓN PARA ELIMINAR ONU
  const handleDeleteONU = async (onuIndex, onuName) => {
    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar la ONU "${onuName}"?\n\nEsta acción no se puede deshacer y eliminará la ONU de la OLT.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      const result = await deleteONU(oltId, onuIndex);
      alert(`✅ ONU "${onuName}" eliminada exitosamente`);

      // Recargar la lista de ONUs
      loadData();
    } catch (error) {
      console.error("Error eliminando ONU:", error);
      alert(`❌ Error al eliminar la ONU: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!oltId) return;
    loadData();
  }, [oltId, loadData]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({
      status: statusFilter,
      power: powerFilter,
    });
  };

  // ========================
  if (loading) {
    return (
      <div className="text-center mt-10 text-gray-500">Cargando ONUs…</div>
    );
  }

  return (
    <div className="p-4">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
      >
        ← Volver al dashboard
      </button>

      <h1 className="text-2xl font-bold mb-4">
        ONUs de <span className="text-blue-600">{oltName}</span> (Total: {total})
      </h1>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 mb-4">
        <select
          className="border rounded px-3 py-1"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>

        <select
          className="border rounded px-3 py-1"
          value={powerFilter}
          onChange={(e) => setPowerFilter(e.target.value)}
        >
          <option value="all">Potencia (todas)</option>
          <option value="bad">Fuera de rango</option>
        </select>

        <input
          type="text"
          placeholder="Buscar ONU / Cliente"
          className="border rounded px-3 py-1 flex-1 min-w-[220px]"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              setSearch(searchInput);
            }
          }}
        />

        <button
          onClick={applyFilters}
          className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Aplicar filtros
        </button>

        <select
          className="border rounded px-3 py-1"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          <option value={50}>50 por página</option>
          <option value={100}>100 por página</option>
          <option value={200}>200 por página</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto bg-white rounded-xl shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2">ONU</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">RX (dBm)</th>
              <th className="px-3 py-2">Alerta</th>
              <th className="px-3 py-2">Acciones</th>
            </tr>
          </thead>

          <tbody>
            {onus.length > 0 ? (
              onus.map((onu) => {
                const rx = onu.rx_power !== null ? Number(onu.rx_power) : null;
                const badPower = rx !== null && (rx < LOW_SIGNAL_MIN || rx > LOW_SIGNAL_MAX);

                return (
                  <tr key={onu.index} className="border-t">
                    <td className="px-3 py-2 font-mono">{onu.onu_id}</td>
                    <td className="px-3 py-2">{onu.name || "N/A"}</td>
                    <td className="px-3 py-2">
                      {onu.status === "online" ? (
                        <span className="text-green-700 font-semibold">🟢 Online</span>
                      ) : (
                        <span className="text-red-700 font-semibold">🔴 Offline</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{rx ?? "N/A"} {rx !== null && "dBm"}</td>
                    <td className="px-3 py-2">
                      {badPower && <span className="text-red-600 font-bold">⚠️ Fuera de rango</span>}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => navigate(`/olts/${oltId}/onus/${onu.fsp_onu || onu.index}`)}
                        className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 mr-2"
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => handleDeleteONU(onu.index, onu.name)}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                        title="Eliminar ONU"
                      >
                        🗑️ Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="px-3 py-6 text-center text-gray-500">
                  No hay ONUs que coincidan con el filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex justify-between items-center mt-4">
        <button
          disabled={page <= 1}
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          ← Anterior
        </button>

        <span>
          Página {page} de {pages} ({total} ONUs)
        </span>

        <button
          disabled={page >= pages}
          onClick={() => setPage((prev) => Math.min(prev + 1, pages))}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
}
